// Fetch AI reasoning summaries for top result cards within a container element.
// Caches summaries by query+article URL so the same article gets consistent copy
// across scope toggles ("All editorial brands" vs "current site").
(() => {
  // --- Caches ---
  // Saves summary text by query + article URL.
  const summaryCache = new Map();
  // Keeps track of requests already in progress so we do not send duplicates.
  const pending = new Map();
  // Tracks how many times we retried one visible set of cards.
  const retryCounts = new Map();
  const DEFAULT_AI_ICON = '/static/images/ai_icon.svg';
  const MAX_SECTION_RETRIES = 2;
  const SECTION_SELECTOR = '.semantic-search-results[data-reasoning-url]';
  // --- Key helpers ---
  // These helpers make sure cache lookups use consistent key strings.

  const norm = (value) => (value || '').trim();
  // data-* attrs use Django escapejs (\u0022 etc.); JSON.parse understands \uXXXX.
  const decodeJsEscapes = (value) => {
    const s = norm(value);
    if (!s) return '';
    try {
      return JSON.parse(`"${s}"`);
    } catch {
      return s;
    }
  };
  const queryKey = (query) => norm(query).toLowerCase();
  // Cache key for one card summary.
  const summaryKey = (query, url) => `${queryKey(query)}::${norm(url)}`;
  // Cache key for the cards currently shown in this section.
  const sectionKey = (query, cards) => `${queryKey(query)}::${cards.map(({ url }) => url).join('|')}`;
  // Find the reasoning section either directly (if container is already the section)
  // or by searching inside the given container.

  const getSection = (container) => {
    if (container && typeof container.matches === 'function' && container.matches(SECTION_SELECTOR)) {
      return container;
    }
    if (container && typeof container.querySelector === 'function') {
      return container.querySelector(SECTION_SELECTOR);
    }
    return null;
  };

  // If scope toggling replaced DOM nodes, find the current live section for this query.
  const getLiveSectionForQuery = (query) => {
    const live = document.querySelector(SECTION_SELECTOR);
    return live && norm(live.dataset.query) === query ? live : null;
  };
  // Hide one card loader when summary is ready (or when retries stop).

  const hideLoader = (card) => {
    const loader = card.querySelector('.semantic-search-card__loading');
    if (loader) loader.style.setProperty('display', 'none');
  };

  const hideAllLoaders = (cards) => cards.forEach(({ card }) => hideLoader(card));
  // Render one summary line; AI icon only for generated reasoning (not content_summary fallback).

  const renderSummary = (card, iconSrc, summary, showIcon) => {
    const summaryEl = card.querySelector('.semantic-search-card__summary');
    if (!summaryEl || !summary) return;
    const iconHtml = showIcon
      ? `<img src="${iconSrc}" alt="" aria-hidden="true" class="semantic-search-card__ai-icon"> `
      : '';
    summaryEl.innerHTML = `${iconHtml}<span>${summary}</span>`;
    summaryEl.style.display = 'flex';
  };
  // Read title/url metadata from visible cards and attach a cache key per card.

  const getCards = (section, query) => Array.from(section.querySelectorAll('.semantic-search-card[data-card-index]'))
    .map((card) => {
      const link = card.querySelector('.semantic-search-card__link');
      const title = link ? norm(link.dataset.cardTitle) : '';
      const url = link ? norm(link.dataset.cardUrl) : '';
      const summary = link ? decodeJsEscapes(link.dataset.cardSummary) : '';
      const contentPublishDate = link ? norm(link.dataset.cardPublishDate) : '';
      const topChunkText = link ? norm(decodeJsEscapes(link.dataset.cardTopChunkText)) : '';
      return {
        card,
        title,
        url,
        summary,
        contentPublishDate,
        topChunkText,
        key: summaryKey(query, url),
      };
    })
    .filter(({ url }) => Boolean(url));

  // Try rendering from cache first.
  // If force=true, loaders are hidden even when not all summaries are ready.
  const applyCached = (cards, iconSrc, force = false) => {
    const allReady = cards.every(({ key }) => summaryCache.has(key));
    if (!force && !allReady) return false;
    cards.forEach(({ card, key }) => {
      const entry = summaryCache.get(key);
      if (entry?.text) renderSummary(card, iconSrc, entry.text, entry.showIcon);
      hideLoader(card);
    });
    return allReady;
  };

  // Main function used on first page load and after scope/result swaps.
  window.fetchSearchReasoning = (container) => {
    const section = getSection(container);
    const query = section ? norm(section.dataset.query) : '';
    if (!section || !query) return Promise.resolve();

    const iconSrc = section.dataset.aiIconSrc || DEFAULT_AI_ICON;
    const cards = getCards(section, query);
    if (!cards.length) return Promise.resolve();

    // Key representing this currently-visible set of cards.
    const cardsKey = sectionKey(query, cards);
    // Retry helper: reruns fetch logic for the same section a limited number of times.
    const retryRender = () => {
      const retries = retryCounts.get(cardsKey) || 0;
      if (retries < MAX_SECTION_RETRIES && section.isConnected) {
        retryCounts.set(cardsKey, retries + 1);
        return window.fetchSearchReasoning(section);
      }
      applyCached(cards, iconSrc, true);
      retryCounts.delete(cardsKey);
      hideAllLoaders(cards);
      return Promise.resolve();
    };

    // Only request summaries that are not already cached.
    const missingCards = cards.filter(({ key }) => !summaryCache.has(key));
    if (!missingCards.length) {
      applyCached(cards, iconSrc, true);
      retryCounts.delete(cardsKey);
      return Promise.resolve();
    }

    // Key for this exact request (same query + same missing card URLs).
    const requestKey = `${queryKey(query)}::${missingCards.map(({ url }) => url).join('|')}`;
    // If this same request is already in flight, reuse it instead of duplicating.
    if (pending.has(requestKey)) {
      return pending.get(requestKey)
        .then(() => (applyCached(cards, iconSrc) ? Promise.resolve() : retryRender()))
        .finally(() => {
          if (applyCached(cards, iconSrc)) {
            retryCounts.delete(cardsKey);
            hideAllLoaders(cards);
          }
        });
    }
    // Send one request for all currently missing card summaries.
    const request = fetch(section.dataset.reasoningUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]')?.value || '',
      },
      body: JSON.stringify({
        query,
        top_matches: missingCards.map(
          ({
            title,
            url,
            summary,
            contentPublishDate,
            topChunkText,
          }) => ({
            title,
            url,
            summary,
            contentPublishDate,
            topChunkText,
          }),
        ),
      }),
    })
      .then(async (response) => {
        // Parse response manually so we can surface a clear non-JSON error.
        const text = await response.text();
        let data = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch (e) {
          throw new Error('Invalid response format (not JSON).');
        }
        if (!response.ok) throw new Error(data.error || `Error ${response.status}`);
        return data;
      })
      .then((data) => {
        // Store returned summaries in cache for reuse across scope toggles.
        // Cache empty strings too (e.g. timeout fallback with no content_summary)
        // so we stop retrying and hide the loader instead of spinning forever.
        // Timeout returns the content_summary we sent — hide the AI icon in that case.
        const contentSummaryByKey = new Map(cards.map(({ key, summary }) => [key, summary]));
        (data.top_matches || []).forEach((match) => {
          const url = match ? norm(match.url) : '';
          if (!url) return;
          const text = match ? decodeJsEscapes(match.summary) : '';
          const key = summaryKey(query, url);
          if (summaryCache.has(key)) return;
          const isFallback = text === (contentSummaryByKey.get(key) || '');
          summaryCache.set(key, { text, showIcon: Boolean(text) && !isFallback });
        });

        if (applyCached(cards, iconSrc)) {
          retryCounts.delete(cardsKey);
        } else {
          // Some card URLs were not in the response; retry a limited number of times.
          // Clear pending first so retry does not wait on this same in-flight request.
          pending.delete(requestKey);
          return retryRender();
        }

        if (!section.isConnected) {
          // If this section was replaced, rerender summaries into the live one.
          const liveSection = getLiveSectionForQuery(query);
          if (liveSection) return window.fetchSearchReasoning(liveSection);
        }
        return Promise.resolve();
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('REASONING API ERROR:', e);
        // Retry path also handles hiding loaders once retry limit is reached.
        pending.delete(requestKey);
        return retryRender();
      })
      .finally(() => {
        // Request finished: clear pending entry and clean up loaders when possible.
        pending.delete(requestKey);
        if (applyCached(cards, iconSrc)) {
          retryCounts.delete(cardsKey);
          hideAllLoaders(cards);
        }
      });

    // Mark this request as in-flight for dedupe.
    pending.set(requestKey, request);
    return request;
  };

  // Run on initial page load
  window.fetchSearchReasoning(document);
})();
